import React, { useContext, useEffect, useMemo, useState } from 'react'
import { alpha, Box, Card, ClickAwayListener, FormControlLabel, Popover, Stack, Switch, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import nbLocale from '@fullcalendar/core/locales/nb'
import type { EventClickArg, EventInput, ViewMountArg } from '@fullcalendar/core'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { DateClickArg } from '@fullcalendar/interaction'
import { GridBreakpoint } from '../../../interfaces/IGridSettings'
import SearchBar from '../SearchBar'
import { CustomContainer } from '../../../components/Wrapper/CustomContainer'
import CalendarEventPopover from './CalendarEventPopover'
import CalendarNewEventPopover from './CalendarNewEventPopover'
import { EventsContext } from '../EventsProvider'
import FilterEventsButtons from '../FilterEventsButtons'
import { bucketColor } from '../../Dashboard/components/BucketColors'
import { EventStatus } from '../../../interfaces/IEventEnums'


import Collapse from '@mui/material/Collapse'
import Button from '@mui/material/Button'

type PopoverType = 'event' | 'newEvent'

const FullcalendarStylingWrapper = styled(Box)(({ theme }) => ({
  '.fc-button.fc-prev-button, .fc-button.fc-next-button, .fc-button.fc-button-primary': {
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '.fc-button:hover': {
    background: theme.palette.primary.dark,
    borderColor: theme.palette.primary.dark,
  },
  '.fc-button-primary:not(:disabled).fc-button-active, .fc .fc-button-primary:not(:disabled):active': {
    backgroundColor: theme.palette.primary.light,
    borderColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
  },
  '.eventlog-event-closed': {
    opacity: 0.45,
    filter: 'grayscale(0.4)',
  },
  '.eventlog-event-closed .fc-event-title, .eventlog-event-closed .fc-event-time': {
    textDecoration: 'line-through',
  },
}))

const EventFullCalendar: React.FC = () => {
  const [isPopperOpen, setIsPopperOpen] = React.useState(false)
  const [popoverType, setPopoverType] = React.useState<PopoverType>()
  const [mouseX, setMouseX] = useState<number>(0)
  const [mouseY, setMouseY] = useState<number>(0)
  const [calendarView, setCalendarView] = useState<ViewMountArg | null>(null)
  const [eventInfo, setEventInfo] = useState<EventClickArg>()
  const [dateClickedInfo, setDateClickedInfo] = useState<DateClickArg>()
  const { handleFilterChange, filteredEvents, searchedEvents, selectedFilter } = useContext(EventsContext)
  const theme = useTheme()
  const [showAllLongEvents, setShowAllLongEvents] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const LONG_EVENT_DAYS = 10

  const visibleEvents = useMemo(
    () => filteredEvents.filter((e) => showClosed || e.status !== EventStatus.Lukket),
    [filteredEvents, showClosed],
  )
  const getDurationInDays = React.useCallback((start: string | Date, end: string | Date) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
  }, [])

  const longEventsAll = React.useMemo(
    () =>
      visibleEvents.filter((e) => {
        if (!e.fromDate || !e.toDate) return false
        return getDurationInDays(e.fromDate, e.toDate) > LONG_EVENT_DAYS
      }),
    [visibleEvents, getDurationInDays, LONG_EVENT_DAYS],
  )

  const shortEvents = React.useMemo(
    () =>
      visibleEvents.filter((e) => {
        if (!e.fromDate || !e.toDate) return true
        return getDurationInDays(e.fromDate, e.toDate) <= LONG_EVENT_DAYS
      }),
    [visibleEvents, getDurationInDays, LONG_EVENT_DAYS],
  )

  const smDown = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (smDown && calendarView?.view.calendar) {
      calendarView?.view.calendar.changeView('dayGridDay')
    }

    if (!smDown && calendarView?.view.calendar) {
      calendarView?.view.calendar.changeView('dayGridMonth')
    }
  }, [smDown])

  useEffect(() => {
    if (searchedEvents.length > 0 && selectedFilter === 'search') {
      handleFilterChange('search')
    }
  }, [searchedEvents, selectedFilter])

  const getEventTypeColor = (eventType: string) => {
    if (eventType === 'Fellesfeil') {
      return theme.palette.eventType.fellesfeil
    }

    if (eventType === 'Planlagt') {
      return theme.palette.eventType.planlagt
    }
    return theme.palette.eventType.generell
  }

  const isEventOverSeveralDays = (event: EventInput) => {
    const eventStartDate = new Date(event.fromDate)
    const eventEndDate = new Date(event.toDate)
    return eventStartDate.getDate() !== eventEndDate.getDate()
  }

  const formatEventToAndFromTime = (event: EventInput) => {
    const eventStartDate = new Date(event.fromDate)
    if (event.toDate) {
      const eventEndDate = new Date(event.toDate)
      return `${eventStartDate.toLocaleDateString([], { day: '2-digit', month: 'short' })} - ${eventEndDate.toLocaleDateString([], { day: '2-digit', month: 'short' })}`
    }

    return `${eventStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - `
  }

  const eventLogEventTransformer = (event: EventInput) => {
    const isOverSeveralDays = isEventOverSeveralDays(event)
    const endDate = new Date(event.toDate)
    const isClosed = event.status === EventStatus.Lukket
    return {
      start: event.fromDate,
      end: isOverSeveralDays ? new Date(endDate).setDate(endDate.getDate() + 1) : event.toDate,
      eventStartDate: event.fromDate,
      eventEndDate: event.toDate,
      eventTitle: event.title,
      id: event.id,
      title: isOverSeveralDays ? `${formatEventToAndFromTime(event)} ${event.title}` : event.title,
      type: event.type,
      allDay: isOverSeveralDays,
      responsible: event.responsible,
      affectedEntitiesCount: event.affectedEntitiesCount,
      nextFollowup: event.nextFollowup,
      createdOn: event.createdOn,
      status: event.status,
      criticality: event.criticality,
      backgroundColor: getEventTypeColor(event.type),
      borderColor: bucketColor(event.criticality),
      classNames: isClosed ? ['eventlog-event-closed'] : [],
    }
  }

  const handleEventClicked = (info: EventClickArg) => {
    info.jsEvent.preventDefault()
    setMouseX(info.jsEvent.clientX)
    setMouseY(info.jsEvent.clientY)
    setPopoverType('event')
    setEventInfo(info)
    setIsPopperOpen(true)
  }

  const handleDateClick = (info: DateClickArg) => {
    setMouseX(info.jsEvent.clientX ?? info.dayEl.getBoundingClientRect().left)
    setMouseY(info.jsEvent.clientY ?? info.dayEl.getBoundingClientRect().top)
    setPopoverType('newEvent')
    setDateClickedInfo(info)
    setIsPopperOpen(true)
  }

  type DateLike = string | Date
  const isEventInView = (eventStart: DateLike, eventEnd: DateLike, viewStart: Date, viewEnd: Date) => {
    const start = new Date(eventStart)
    const end = new Date(eventEnd)
    const vEnd = new Date(viewEnd)
    vEnd.setDate(vEnd.getDate() - 1)
    return start <= vEnd && end >= viewStart
  }

  const [calendarRange, setCalendarRange] = useState<{ start: Date; end: Date } | null>(null)

  const visibleLongEvents = React.useMemo(() => {
    if (!calendarRange) return longEventsAll
    return longEventsAll.filter((e) => e.fromDate && e.toDate && isEventInView(e.fromDate, e.toDate, calendarRange.start, calendarRange.end))
  }, [longEventsAll, calendarRange])

  const maxLongEventsToShow = 3
  const hasMoreLongEvents = visibleLongEvents.length > maxLongEventsToShow
  const longEventsToShow = showAllLongEvents ? visibleLongEvents : visibleLongEvents.slice(0, maxLongEventsToShow)

  const handleDatesSet = React.useCallback((arg: any) => {
    setCalendarRange({ start: arg.start, end: arg.end })
  }, [])

  return (
    <CustomContainer gridWidth={GridBreakpoint.xxl}>
      <SearchBar />
      <FilterEventsButtons onFilterChange={handleFilterChange} />
      <Stack direction="row" sx={{ justifyContent: 'flex-end', px: 2, py: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={showClosed} onChange={(_, checked) => setShowClosed(checked)} />}
          label="Vis lukkede"
        />
      </Stack>
      <Stack direction="column" spacing={1}>
        <FullcalendarStylingWrapper sx={{ p: 2, backgroundColor: theme.palette.background.paper }}>
          {/* Egen seksjon for langvarige hendelser */}
          {visibleLongEvents.length > 0 && (
            <Card sx={{ mb: 2, p: 2, background: alpha(theme.palette.primary.light, 0.1) }} elevation={0}>
              <Typography variant={'body1'} gutterBottom sx={{
                fontWeight: "bold"
              }}>
                Langvarige hendelser i denne perioden (&gt; {LONG_EVENT_DAYS} dager):
              </Typography>
              <Collapse in={true} timeout="auto">
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {longEventsToShow.map((event) => (
                    <li
                      key={event.id}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        outline: 'none',
                        borderRadius: 4,
                        padding: '2px 2px',
                        transition: 'background 0.15s',
                      }}
                      tabIndex={0}
                      onMouseOver={(e) => (e.currentTarget.style.background = theme.palette.action.hover)}
                      onMouseOut={(e) => (e.currentTarget.style.background = '')}
                      onFocus={(e) => (e.currentTarget.style.background = theme.palette.action.hover)}
                      onBlur={(e) => (e.currentTarget.style.background = '')}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        window.open(`/events/${event.id}`, '_blank', 'noopener,noreferrer')
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          window.open(`/events/${event.id}`, '_blank', 'noopener,noreferrer')
                        }
                      }}
                      aria-label={`G� til hendelse ${event.title}`}
                    >
                      <span style={{ fontWeight: 500 }}>{event.title}</span> &nbsp;
                      <span>
                        ({new Date(event.fromDate).toLocaleDateString()} - {event.toDate ? new Date(event.toDate).toLocaleDateString() : ''})
                      </span>
                      {event.type && <span style={{ marginLeft: 8, color: getEventTypeColor(event.type) }}>[{event.type}]</span>}
                      {event.status && <span style={{ marginLeft: 8 }}>[{event.status}]</span>}
                    </li>
                  ))}
                </ul>
              </Collapse>
              {hasMoreLongEvents && !showAllLongEvents && (
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setShowAllLongEvents(true)}>
                  Vis alle langvarige hendelser ({visibleLongEvents.length})
                </Button>
              )}
              {hasMoreLongEvents && showAllLongEvents && (
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setShowAllLongEvents(false)}>
                  Vis f&aelig;rre
                </Button>
              )}
            </Card>
          )}
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={smDown ? 'dayGridDay' : 'dayGridMonth'}
            viewDidMount={(view) => setCalendarView(view)}
            datesSet={handleDatesSet}
            nowIndicator={true}
            eventTimeFormat={{ hour: 'numeric', minute: '2-digit', hour12: false, meridiem: false }}
            locale={nbLocale}
            events={shortEvents}
            weekNumbers={true}
            eventOrder={(a: any, b: any) => {
              //make sure to show events with shortest duration in top of each day
              const getTime = (d: any) => (d ? new Date(d).getTime() : 0)
              const durationA = getTime(a.end ?? a.start) - getTime(a.start)
              const durationB = getTime(b.end ?? b.start) - getTime(b.start)
              if (durationA !== durationB) return durationA - durationB
              return getTime(a.start) - getTime(b.start)
            }}
            views={{
              dayGridMonth: {
                titleFormat: { month: 'long' },
                dayMaxEvents: 10,
              },
              dayGridDay: {
                titleFormat: { weekday: 'long', day: 'numeric', month: 'long' },
                dayMaxEvents: 10,
              },
            }}
            headerToolbar={{
              start: 'title',
              center: '',
              end: smDown ? 'prev,next' : 'dayGridMonth,timeGridWeek,prev,next',
            }}
            editable={false}
            titleFormat={{ month: 'long' }}
            displayEventEnd={true}
            eventClick={handleEventClicked}
            dateClick={handleDateClick}
            eventDataTransform={(event) => eventLogEventTransformer(event)}
          />
          <Popover
            open={isPopperOpen}
            sx={{ zIndex: 99999, textWrap: 'balance' }}
            anchorReference="anchorPosition"
            disableAutoFocus={true}
            anchorPosition={{ top: mouseY, left: mouseX }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
          >
            <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
              <Card sx={{ maxWidth: '400px' }}>
                {popoverType === 'event' && eventInfo && <CalendarEventPopover info={eventInfo} />}
                {popoverType === 'newEvent' && dateClickedInfo && <CalendarNewEventPopover info={dateClickedInfo} />}
              </Card>
            </ClickAwayListener>
          </Popover>
        </FullcalendarStylingWrapper>
      </Stack>
    </CustomContainer>
  );
}

export default EventFullCalendar
